import BrandedDesignComparison from './BrandedDesignComparison';
import DesignConcept from "./DesignConcept";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  Modal,
  ModalDialog,
  Radio,
  RadioGroup,
  Sheet,
  Stepper,
  Step,
  StepIndicator,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  Sparkle as AutoAwesomeIcon,
  Check as CheckIcon,
  CaretLeft as BackIcon,
  Desktop as DesktopIcon,
  DeviceMobile as MobileIcon,
  DeviceTablet as TabletIcon,
  ArrowsClockwise as RefreshIcon,
  Rocket as RocketIcon,
  ArrowSquareOut as OpenInNewIcon,
  Code as CodeIcon,
  FloppyDisk as SaveIcon,
  Warning as WarningIcon,
} from '@phosphor-icons/react';
import api from '../../../lib/api';
import CodeEditor from '../../../components/common/CodeEditor';
import { WEBSITE_GENERATING_STATUSES, isRecentlyDeployed } from '../../../types/websiteBuilder';
import type {
  WebsiteProjectDetail,
  WebsiteProjectStatusValue,
} from '../../../types/websiteBuilder';

type SharedChromeField = 'shared_header_html' | 'child_hero_html' | 'shared_footer_html';

/** Assemble a complete HTML document for template preview using blob URL */
function assembleTemplateHtml(
  templateKey: string,
  templateHtml: string,
  project: WebsiteProjectDetail,
): string {
  const prettyName = templateKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const heroHtml = (project.child_hero_html || '')
    .replace(/\{\{hero_heading\}\}/g, prettyName)
    .replace(/\{\{hero_subheading\}\}/g, 'Template Preview')
    .replace(/\{\{breadcrumb\}\}/g, `<a href="/">Home</a> &gt; ${prettyName}`);

  // Replace common content placeholders with sample indicators
  const body = templateHtml
    .replace(/\{\{content_sections\}\}/g, '<div style="padding:2rem;text-align:center;color:#999;border:2px dashed #ddd;border-radius:8px;margin:1rem 0;"><p>Content will be generated here</p></div>')
    .replace(/\{\{faq_items\}\}/g, '<div style="padding:1rem;text-align:center;color:#999;border:2px dashed #ddd;border-radius:8px;margin:1rem 0;"><p>FAQ items will appear here</p></div>')
    .replace(/\{\{service_name\}\}/g, 'Service Name')
    .replace(/\{\{city\}\}/g, 'City')
    .replace(/\{\{state\}\}/g, 'State')
    .replace(/\{\{service_list_html\}\}/g, '')
    .replace(/\{\{related_links\}\}/g, '');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>${project.homepage_css || ''}</style>
</head>
<body>
  ${project.shared_header_html || ''}
  ${heroHtml}
  <main>${body}</main>
  ${project.shared_footer_html || ''}
  <script>${project.homepage_js || ''}</script>
</body>
</html>`;
}

const STEPS = [
  'Style',
  'Preview Data',
  'Generate',
  'Review Design',
  'Generate Site',
  'Review Site',
  'Deploy',
];

function getActiveStep(status: WebsiteProjectStatusValue): number {
  const map: Record<string, number> = {
    questionnaire: 0,
    generating_design_system: 2,
    generating_homepage: 2,
    homepage_review: 3,
    generating_site: 4,
    generating_content: 4,
    assembling: 4,
    site_review: 5,
    deploying: 6,
    deployed: 6,
    failed: 0,
  };
  return map[status] ?? 0;
}

export const WebsiteBuilderWizard: React.FC = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!projectId;

  // Local state for questionnaire
  const [localEvidence, setLocalEvidence] = useState<Record<string, string>>({});
  const [sectionOrder, setSectionOrder] = useState('services_first');
  const [servicesLayout, setServicesLayout] = useState('cards');
  const [designFamily, setDesignFamily] = useState('auto');
  const [layoutStyle, setLayoutStyle] = useState('modern');
  const [heroStyle, setHeroStyle] = useState('full_width_image');
  const [lockedFontPair, setLockedFontPair] = useState('');
  const [lockedPrimaryColor, setLockedPrimaryColor] = useState('');
  const [colorMood, setColorMood] = useState('neutral');
  const [customInstructions, setCustomInstructions] = useState('');
  const [feedback, setFeedback] = useState('');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [previewPageSlug, setPreviewPageSlug] = useState('');
  const [reviewTab, setReviewTab] = useState(0);
  const [editingTemplateKey, setEditingTemplateKey] = useState<string | null>(null);
  const [editedTemplates, setEditedTemplates] = useState<Record<string, string>>({});
  const [editedShared, setEditedShared] = useState<Partial<Record<SharedChromeField, string>>>({});
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [templateViewMode, setTemplateViewMode] = useState<Record<string, 'preview' | 'code'>>({});
  const [templateBlobUrls, setTemplateBlobUrls] = useState<Record<string, string>>({});
  const [confirmRegenDesignOpen, setConfirmRegenDesignOpen] = useState(false);

  // Fetch style options
  const { data: styleOptions } = useQuery({
    queryKey: ['website-builder-style-options'],
    queryFn: async () => {
      const response = await api.websiteBuilder.styleOptions();
      return response.data;
    },
  });

  // Fetch knowledge graph preview
  const { data: knowledgeGraph } = useQuery({
    queryKey: ['website-builder-knowledge-graph'],
    queryFn: async () => {
      const response = await api.websiteBuilder.knowledgeGraph();
      return response.data;
    },
  });

  // Fetch existing project
  const { data: project, refetch: refetchProject } = useQuery({
    queryKey: ['website-builder-project', projectId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getProject(projectId!);
      return response.data;
    },
    enabled: isEditing,
  });

  // Pre-flight readiness check: does the org have enough data for a good site?
  // Only relevant on the questionnaire step (before any generation started).
  const { data: readiness } = useQuery({
    queryKey: ['website-builder-readiness'],
    queryFn: async () => {
      const response = await api.websiteBuilder.readiness();
      return response.data;
    },
    enabled: !isEditing || project?.status === 'questionnaire',
  });
  const failedChecks = (readiness?.checks || []).filter((check) => !check.ok);
  const blockedByReadiness = readiness ? !readiness.ready : false;

  // Poll status during generation
  const { data: projectStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['website-builder-status', projectId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getStatus(projectId!);
      return response.data;
    },
    enabled: isEditing && !!project && WEBSITE_GENERATING_STATUSES.includes(project.status),
    refetchInterval: 5000,
  });

  // Shared error surfacing for all generation mutations. A 409 means a
  // generation is already running server-side — refresh state instead of
  // treating it as a hard failure.
  const handleApiError = (error: unknown, fallback: string) => {
    const err = error as { status?: number; responseData?: { error?: string; detail?: string } };
    if (err?.status === 409) {
      const busyMessage = err?.responseData?.error || 'Generation is already in progress — refreshing status...';
      toast(busyMessage, { icon: '⏳', duration: 5000 });
      refetchProject();
      refetchStatus();
      return;
    }
    toast.error(err?.responseData?.error || err?.responseData?.detail || fallback, { duration: 8000 });
  };

  // Sync project data when status changes
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (projectStatus && project && projectStatus.status !== project.status) {
      refetchProject();
      queryClient.invalidateQueries({ queryKey: ['website-builder-projects'] });
    }
  }, [projectStatus?.status]);

  // Notify user when generation completes (works even if they navigated away and came back)
  useEffect(() => {
    if (!project?.status || !prevStatusRef.current) {
      prevStatusRef.current = project?.status;
      return;
    }
    const prev = prevStatusRef.current;
    const curr = project.status;
    prevStatusRef.current = curr;

    if (prev === 'generating_design_system' && curr === 'generating_homepage') {
      toast.success('Design system created! Now generating your homepage...', { duration: 5000 });
    } else if (prev === 'generating_homepage' && curr === 'homepage_review') {
      toast.success('Your homepage is ready for review!', { duration: 8000 });
    } else if (['generating_site', 'generating_content', 'assembling'].includes(prev || '') && curr === 'site_review') {
      toast.success('Your full website has been generated!', { duration: 8000 });
    } else if (prev === 'deploying' && curr === 'deployed') {
      toast.success('Your website is live!', { duration: 8000 });
    } else if (curr === 'failed' && prev !== 'failed') {
      toast.error('Generation failed. Please try again.', { duration: 8000 });
    }
  }, [project?.status]);

  // Initialize form from existing project
  useEffect(() => {
    if (project?.style_preferences) {
      setLocalEvidence(project.local_evidence || {});
      setSectionOrder(project.style_preferences.section_order || 'services_first');
      setServicesLayout(project.style_preferences.services_layout || 'cards');
      setDesignFamily(project.style_preferences.design_family || 'auto');
      setLayoutStyle(project.style_preferences.layout_style || 'modern');
      setHeroStyle(project.style_preferences.hero_style || 'full_width_image');
      setLockedFontPair(project.style_preferences.locked_font_pair || '');
      setLockedPrimaryColor(project.style_preferences.locked_primary_color || '');
      setColorMood(project.style_preferences.color_mood || 'neutral');
      setCustomInstructions(project.custom_instructions || '');
    }
  }, [project]);

  // Create project mutation
  const createProject = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.createProject({
        name: knowledgeGraph?.company?.name ? `${knowledgeGraph.company.name} Website` : 'My Website',
        local_evidence: localEvidence,
        style_preferences: {
          section_order: sectionOrder,
          services_layout: servicesLayout,
          design_family: designFamily,
          layout_style: layoutStyle,
          hero_style: heroStyle,
          locked_font_pair: lockedFontPair,
          locked_primary_color: lockedPrimaryColor,
          color_mood: colorMood,
        },
        custom_instructions: customInstructions,
      });
      return response.data;
    },
    onSuccess: (data) => {
      navigate(`/marketing/website-builder/${data.id}`, { replace: true });
    },
    onError: (error) => handleApiError(error, 'Failed to create the website project'),
  });

  // Generate homepage mutation. Pass regenerateDesign to rebuild the whole
  // design system; without it an existing design only re-runs homepage copy.
  const generateHomepage = useMutation({
    mutationFn: async (opts: { regenerateDesign?: boolean }) => {
      const id = projectId || project?.id;
      const response = await api.websiteBuilder.generateHomepage(id!, opts);
      return response.data;
    },
    onSuccess: () => {
      refetchProject();
    },
    onError: (error) => handleApiError(error, 'Failed to start homepage generation'),
  });

  // Revise mutation + polling
  const [isRevising, setIsRevising] = useState(false);
  const revisionStartCount = useRef<number>(0);
  const revisionTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const revise = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.revise(projectId!, { feedback });
      return response.data;
    },
    onSuccess: () => {
      setFeedback('');
      revisionStartCount.current = project?.revision_count || 0;
      setIsRevising(true);
      toast('Revision in progress...', { icon: '✏️', duration: 4000 });
      // Safety timeout — clear overlay after 5 minutes even if revision fails silently
      clearTimeout(revisionTimeout.current);
      revisionTimeout.current = setTimeout(() => {
        setIsRevising(false);
        refetchProject();
        toast.error('Revision timed out. Please try again.', { duration: 5000 });
      }, 5 * 60 * 1000);
    },
    onError: (error) => handleApiError(error, 'Failed to start the revision'),
  });

  // Poll for revision completion when revising
  const { data: revisionPollData } = useQuery({
    queryKey: ['website-builder-revision-poll', projectId, revisionStartCount.current],
    queryFn: async () => {
      const response = await api.websiteBuilder.getProject(projectId!);
      return response.data;
    },
    enabled: isRevising && isEditing,
    refetchInterval: 3000,
  });

  // Detect when revision completes (revision_count increases)
  useEffect(() => {
    if (isRevising && revisionPollData) {
      if (revisionPollData.revision_count > revisionStartCount.current) {
        setIsRevising(false);
        clearTimeout(revisionTimeout.current);
        refetchProject();
        toast.success('Revision complete!', { duration: 5000 });
      }
    }
  }, [revisionPollData?.revision_count]);

  // Generate full site mutation
  const generateSite = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.generateSite(projectId!);
      return response.data;
    },
    onSuccess: () => {
      refetchProject();
    },
    onError: (error) => handleApiError(error, 'Failed to start full site generation'),
  });

  // Deploy mutation
  const deploy = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.deploy(projectId!);
      return response.data;
    },
    onSuccess: () => {
      refetchProject();
    },
    onError: (error) => handleApiError(error, 'Failed to start deployment'),
  });

  const activeStep = project ? getActiveStep(project.status) : 0;
  const isGenerating = !!project?.status && WEBSITE_GENERATING_STATUSES.includes(project.status);

  // Live Message-Batch progress: page copy is written in batched groups, so
  // per-page chips stay "Generating..." until each batch lands — this line is
  // what shows movement in the meantime.
  const batchProgress = projectStatus?.batch_progress;
  const batchDisplay = batchProgress && batchProgress.total_requests > 0
    ? `Writing page copy: ${batchProgress.succeeded + batchProgress.errored} of ${batchProgress.total_requests} batches finished`
    : null;

  const previewIframeWidth = previewDevice === 'mobile' ? 375 : previewDevice === 'tablet' ? 768 : 1280;

  // Generate stable blob URLs for template previews
  useEffect(() => {
    if (!project?.page_templates || typeof project.page_templates !== 'object') return;
    const templates = project.page_templates;
    const urls: Record<string, string> = {};
    for (const [key, template] of Object.entries(templates)) {
      const currentHtml = editedTemplates[key] ?? template;
      const fullHtml = assembleTemplateHtml(key, currentHtml, project);
      const blob = new Blob([fullHtml], { type: 'text/html' });
      urls[key] = URL.createObjectURL(blob);
    }
    setTemplateBlobUrls(prev => {
      // Revoke old URLs
      Object.values(prev).forEach(url => URL.revokeObjectURL(url));
      return urls;
    });
    return () => {
      Object.values(urls).forEach(url => URL.revokeObjectURL(url));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.page_templates, project?.homepage_css, project?.shared_header_html, project?.child_hero_html, project?.shared_footer_html, editedTemplates]);

  // Update existing project mutation
  const updateProject = useMutation({
    mutationFn: async () => {
      const id = projectId || project?.id;
      const response = await api.websiteBuilder.updateProject(id!, {
        name: knowledgeGraph?.company?.name ? `${knowledgeGraph.company.name} Website` : project?.name || 'My Website',
        local_evidence: localEvidence,
        style_preferences: {
          section_order: sectionOrder,
          services_layout: servicesLayout,
          design_family: designFamily,
          layout_style: layoutStyle,
          hero_style: heroStyle,
          locked_font_pair: lockedFontPair,
          locked_primary_color: lockedPrimaryColor,
          color_mood: colorMood,
        },
        custom_instructions: customInstructions,
      });
      return response.data;
    },
    onSuccess: () => {
      refetchProject();
    },
    onError: (error) => handleApiError(error, 'Failed to save the project settings'),
  });

  const handleCreateAndGenerate = async () => {
    if (!isEditing) {
      await createProject.mutateAsync().catch(() => {
        // onError already surfaced the failure
      });
    }
    // After redirect, generate will be triggered
  };

  const handleUpdateAndRegenerate = async () => {
    try {
      await updateProject.mutateAsync();
    } catch {
      return; // onError already surfaced the failure
    }
    generateHomepage.mutate({});
  };

  // Retry after a failure: if pages already exist the failure happened during
  // full-site generation — resume it. Otherwise retry the homepage phase.
  const handleRetryAfterFailure = () => {
    if ((project?.pages_count || 0) > 0) {
      generateSite.mutate();
    } else {
      generateHomepage.mutate({});
    }
  };

  // Auto-trigger generate after project creation — but only once the
  // pre-flight readiness check confirms the required org data exists.
  useEffect(() => {
    if (
      project &&
      project.status === 'questionnaire' &&
      readiness?.ready &&
      !generateHomepage.isPending &&
      !generateHomepage.isSuccess
    ) {
      // Project just created, trigger generation
      generateHomepage.mutate({});
    }
  }, [project?.id, project?.status, readiness?.ready]);

  const showStyleEditor = !isEditing || project?.status === 'failed' || project?.status === 'questionnaire';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          variant="plain"
          startDecorator={<BackIcon />}
          onClick={() => navigate('/marketing/website-builder')}
        >
          Back
        </Button>
        <Typography level="h2" startDecorator={<AutoAwesomeIcon />}>
          {isEditing ? project?.name || 'Website Builder' : 'Create New Website'}
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper sx={{ mb: 4 }}>
        {STEPS.map((label, index) => (
          <Step
            key={label}
            indicator={
              <StepIndicator
                variant={index <= activeStep ? 'solid' : 'outlined'}
                color={index < activeStep ? 'success' : index === activeStep ? 'primary' : 'neutral'}
              >
                {index < activeStep ? <CheckIcon /> : index + 1}
              </StepIndicator>
            }
          >
            {label}
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}

      {/* FAILED - inline error banner above style editor */}
      {project?.status === 'failed' && (
        <Alert color="danger" variant="soft" sx={{ mb: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography level="title-md" color="danger">
              Generation Failed
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {project.last_error
                || projectStatus?.error
                || 'An error occurred during generation. Update your settings below and try again.'}
            </Typography>
          </Box>
          <Button
            size="sm"
            color="danger"
            variant="solid"
            startDecorator={<RefreshIcon />}
            onClick={handleRetryAfterFailure}
            loading={generateHomepage.isPending || generateSite.isPending}
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Readiness pre-flight: org data checks before generation */}
      {showStyleEditor && failedChecks.length > 0 && (
        <Alert
          color={blockedByReadiness ? 'danger' : 'warning'}
          variant="soft"
          sx={{ mb: 2, alignItems: 'flex-start' }}
          startDecorator={<WarningIcon size={20} />}
        >
          <Box sx={{ flex: 1 }}>
            <Typography level="title-md" color={blockedByReadiness ? 'danger' : 'warning'}>
              {blockedByReadiness
                ? 'Required company data is missing'
                : 'Your website will be better with more company data'}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {blockedByReadiness
                ? 'Add the required items below before generating your website.'
                : 'These are optional, but adding them makes the generated site noticeably better.'}
            </Typography>
            <List size="sm" sx={{ '--List-padding': '0px', mt: 1 }}>
              {failedChecks.map((check) => (
                <ListItem key={check.key} sx={{ alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-sm">
                      {check.label}
                      {check.required && (
                        <Chip size="sm" color="danger" variant="outlined" sx={{ ml: 1 }}>
                          Required
                        </Chip>
                      )}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {check.message}
                    </Typography>
                  </Box>
                  <Link
                    component={RouterLink}
                    to={check.fix_url}
                    level="body-sm"
                    sx={{ whiteSpace: 'nowrap', ml: 1 }}
                  >
                    Fix this →
                  </Link>
                </ListItem>
              ))}
            </List>
          </Box>
        </Alert>
      )}

      {project?.design_system?.similarity_report && (
        <Alert color={project.design_system.similarity_report.status === 'distinct' ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {project.design_system.similarity_report.message}
        </Alert>
      )}
      {/* STEP 0: Style Questionnaire */}
      {showStyleEditor && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h3" sx={{ mb: 3 }}>
              Choose Your Website Style
            </Typography>

            <Grid container spacing={3}>
              <Grid xs={12}>
                <Typography level="title-md">Local project facts</Typography>
                <Typography level="body-sm">Add real project examples and service details for each city. Cities with at least 120 characters of local facts receive a dedicated page; all cities remain in your coverage list.</Typography>
                {(knowledgeGraph?.areas_served || []).map((area: { city: string; state: string }) => {
                  const key = `${area.city}, ${area.state}`;
                  return <FormControl key={key} sx={{ mt: 1 }}><FormLabel>{key}</FormLabel>
                    <Textarea minRows={2} value={localEvidence[key] || ''} onChange={e => setLocalEvidence(prev => ({ ...prev, [key]: e.target.value }))} placeholder="Describe completed work, materials used, local service experience and any photo context." />
                  </FormControl>;
                })}
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl><FormLabel>Homepage Story</FormLabel>
                  <RadioGroup value={sectionOrder} onChange={(e) => setSectionOrder(e.target.value)}>
                    <Radio value="services_first" label="Lead with services" /><Radio value="projects_first" label="Lead with projects" /><Radio value="story_first" label="Lead with our story" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl><FormLabel>Service Presentation</FormLabel>
                  <RadioGroup value={servicesLayout} onChange={(e) => setServicesLayout(e.target.value)}>
                    <Radio value="cards" label="Cards" /><Radio value="rows" label="Alternating rows" /><Radio value="numbered" label="Numbered list" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <BrandedDesignComparison data={knowledgeGraph} family={designFamily} onFamily={setDesignFamily} fontPair={lockedFontPair} onFontPair={setLockedFontPair} primaryColor={lockedPrimaryColor} onPrimaryColor={setLockedPrimaryColor} heroStyle={heroStyle} />
                  <FormLabel>Design Direction</FormLabel>
                  <RadioGroup value={designFamily} onChange={(e) => setDesignFamily(e.target.value)} sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,260px),1fr))', gap: 1 }}>
                    {(styleOptions?.design_families || []).map((opt) => (
                      <Sheet key={opt.value} variant="outlined" sx={{ p: 2, mb: 1, borderRadius: 'md' }}>
                        <DesignConcept family={opt.value} />
                        <Typography level="body-xs">Layout concept</Typography>
                        <Radio value={opt.value} label={<Box><Typography level="title-sm" sx={{ display: 'block' }}>{opt.label}</Typography><Typography level="body-xs" sx={{ display: 'block', mt: 0.5 }}>{opt.description}</Typography></Box>} />
                      </Sheet>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Layout Style</FormLabel>
                  <RadioGroup
                    value={layoutStyle}
                    onChange={(e) => setLayoutStyle(e.target.value)}
                    sx={{ gap: 1 }}
                  >
                    {(styleOptions?.layout_styles || []).map((opt) => (
                      <Sheet key={opt.value} variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Radio
                          value={opt.value}
                          label={
                            <Box>
                              <Typography level="title-sm">{opt.label}</Typography>
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {opt.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Sheet>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Hero Section Style</FormLabel>
                  <RadioGroup
                    value={heroStyle}
                    onChange={(e) => setHeroStyle(e.target.value)}
                    sx={{ gap: 1 }}
                  >
                    {(styleOptions?.hero_styles || []).map((opt) => (
                      <Sheet key={opt.value} variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Radio
                          value={opt.value}
                          label={
                            <Box>
                              <Typography level="title-sm">{opt.label}</Typography>
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {opt.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Sheet>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Color Mood</FormLabel>
                  <RadioGroup
                    value={colorMood}
                    onChange={(e) => setColorMood(e.target.value)}
                    sx={{ gap: 1 }}
                  >
                    {(styleOptions?.color_moods || []).map((opt) => (
                      <Sheet key={opt.value} variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Radio
                          value={opt.value}
                          label={
                            <Box>
                              <Typography level="title-sm">{opt.label}</Typography>
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {opt.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Sheet>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Additional Instructions (Optional)</FormLabel>
                  <Textarea
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="Any specific preferences? E.g., 'Make the hero section emphasize emergency services' or 'Use a dark theme throughout'"
                    minRows={3}
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Knowledge Graph Preview */}
            {knowledgeGraph && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="h4" sx={{ mb: 2 }}>
                  Data We&apos;ll Use
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={12} md={6}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Company</Typography>
                      <Typography level="body-sm">{knowledgeGraph.company?.name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {knowledgeGraph.company?.description?.substring(0, 100)}...
                      </Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Services</Typography>
                      <Typography level="body-sm">{knowledgeGraph.services?.length || 0} services</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Areas</Typography>
                      <Typography level="body-sm">{knowledgeGraph.areas_served?.length || 0} areas</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Reviews</Typography>
                      <Typography level="body-sm">{knowledgeGraph.reviews?.length || 0} reviews</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Photos</Typography>
                      <Typography level="body-sm">{knowledgeGraph.photos?.length || 0} photos</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Brand Colors</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {(knowledgeGraph.branding?.colors || []).map((color: string, i: number) => (
                          <Box
                            key={i}
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: 'sm',
                              bgcolor: color,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        ))}
                      </Box>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} md={3}>
                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                      <Typography level="title-sm">Blog Posts</Typography>
                      <Typography level="body-sm">{knowledgeGraph.articles?.length || 0} articles</Typography>
                    </Sheet>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              {blockedByReadiness && (
                <Typography level="body-sm" color="danger">
                  Add the required company data above before generating your website.
                </Typography>
              )}
              {isEditing ? (
                <Button
                  size="lg"
                  startDecorator={<RefreshIcon />}
                  onClick={handleUpdateAndRegenerate}
                  loading={updateProject.isPending || generateHomepage.isPending}
                  disabled={blockedByReadiness}
                >
                  Update Settings & Regenerate
                </Button>
              ) : (
                <Button
                  size="lg"
                  startDecorator={<AutoAwesomeIcon />}
                  onClick={handleCreateAndGenerate}
                  loading={createProject.isPending}
                  disabled={blockedByReadiness}
                >
                  Generate Website
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* GENERATING STATE */}
      {isGenerating && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            {/* Homepage / deploying: simple spinner */}
            {!['generating_site', 'generating_content', 'assembling'].includes(project?.status || '') && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size="lg" sx={{ mb: 3 }} />
                <Typography level="h3" sx={{ mb: 1 }}>
                  {project?.status === 'generating_design_system'
                    ? 'ApexOS is Creating Your Design System...'
                    : project?.status === 'generating_homepage'
                    ? 'ApexOS is Designing Your Homepage...'
                    : 'ApexOS is Deploying Your Website...'}
                </Typography>
                <Typography level="body-md" sx={{ color: 'text.secondary', mb: 2 }}>
                  {project?.status === 'generating_design_system'
                    ? 'Our AI is building a unique design system with custom colors, typography, templates, and shared components for your website. This may take up to 3 minutes.'
                    : project?.status === 'generating_homepage'
                    ? 'Our AI is crafting a custom homepage using your brand colors, services, reviews, and photos. This may take up to 5 minutes.'
                    : 'Publishing your website to the web via S3 and CloudFront CDN.'}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Feel free to navigate to other pages — we&apos;ll notify you when it&apos;s ready.
                </Typography>
              </Box>
            )}

            {/* Full site generation: per-page status table */}
            {['generating_site', 'generating_content', 'assembling'].includes(project?.status || '') && (
              <Box>
                <Typography level="h3" sx={{ mb: 1 }}>
                  ApexOS is Building Your Full Website...
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: batchDisplay ? 0.5 : 2 }}>
                  Page copy is written in batched groups, so pages complete together
                  as each batch finishes — feel free to navigate away; we&apos;ll notify
                  you when it&apos;s ready.
                </Typography>
                {batchDisplay && (
                  <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                    {batchDisplay}
                  </Typography>
                )}

                {projectStatus?.pages_progress && (
                  <Box sx={{ mb: 3 }}>
                    <LinearProgress
                      determinate
                      value={projectStatus.pages_progress.percentage}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography level="body-sm">
                        {projectStatus.pages_progress.completed} / {projectStatus.pages_progress.total} pages generated
                        {projectStatus.pages_progress.failed > 0 && (
                          <Chip size="sm" color="danger" sx={{ ml: 1 }}>
                            {projectStatus.pages_progress.failed} failed
                          </Chip>
                        )}
                      </Typography>
                      {(projectStatus.pages_progress.failed > 0 || projectStatus.pages_progress.pending > 0) && (
                        <Button
                          size="sm"
                          variant="soft"
                          onClick={async () => {
                            try {
                              await api.websiteBuilder.generateRemaining(projectId!);
                            } catch (error) {
                              handleApiError(error, 'Failed to queue the remaining pages');
                            }
                            refetchStatus();
                          }}
                        >
                          Generate All Remaining
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Per-page status table */}
                {projectStatus?.pages && (
                  <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                    {projectStatus.pages.map((page) => (
                      <Box
                        key={page.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography level="body-sm" noWrap fontWeight="md">
                            {page.title}
                          </Typography>
                          {page.slug && (
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              /{page.slug}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={
                              page.status === 'generated' ? 'success'
                                : page.status === 'generating' ? 'primary'
                                : page.status === 'failed' ? 'danger'
                                : 'neutral'
                            }
                            startDecorator={page.status === 'generating' ? <CircularProgress size="sm" thickness={2} /> : undefined}
                          >
                            {page.status === 'generated' ? 'Generated'
                              : page.status === 'generating' ? 'Generating...'
                              : page.status === 'failed' ? 'Failed'
                              : 'Pending'}
                          </Chip>
                          {(page.status === 'failed' || page.status === 'pending') && (
                            <Button
                              size="sm"
                              variant="plain"
                              color={page.status === 'failed' ? 'danger' : 'primary'}
                              onClick={async () => {
                                try {
                                  await api.websiteBuilder.generatePage(projectId!, page.id);
                                } catch (error) {
                                  handleApiError(error, `Failed to start generation for "${page.title}"`);
                                }
                                refetchStatus();
                              }}
                            >
                              {page.status === 'failed' ? 'Retry' : 'Generate'}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Sheet>
                )}

                {/* All done — continue */}
                {projectStatus?.pages_progress &&
                  projectStatus.pages_progress.completed === projectStatus.pages_progress.total && (
                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Button
                      size="lg"
                      onClick={() => refetchProject()}
                    >
                      Continue to Review →
                    </Button>
                  </Box>
                )}
                {/* Some done, some failed — offer to continue */}
                {projectStatus?.pages_progress &&
                  projectStatus.pages_progress.completed > 0 &&
                  projectStatus.pages_progress.failed > 0 &&
                  projectStatus.pages_progress.pending === 0 && (
                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Button
                      variant="plain"
                      onClick={() => refetchProject()}
                    >
                      Continue with Generated Pages
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* HOMEPAGE REVIEW */}
      {project?.status === 'homepage_review' && (
        <Box>
          <Tabs value={reviewTab} onChange={(_e, val) => setReviewTab(val as number)} sx={{ mb: 2 }}>
            <TabList>
              <Tab>Homepage Preview</Tab>
              <Tab>Page Templates {project.page_templates && Object.keys(project.page_templates).length > 0 && (
                <Chip size="sm" sx={{ ml: 1 }}>{Object.keys(project.page_templates).length}</Chip>
              )}</Tab>
              <Tab>Shared Components</Tab>
            </TabList>

            {/* TAB 0: Homepage Preview */}
            <TabPanel value={0} sx={{ p: 0 }}>
              <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="h3">Homepage Preview</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {(['desktop', 'tablet', 'mobile'] as const).map((device) => (
                      <Button
                        key={device}
                        variant={previewDevice === device ? 'solid' : 'outlined'}
                        size="sm"
                        onClick={() => setPreviewDevice(device)}
                      >
                        {device === 'desktop' ? <DesktopIcon /> : device === 'tablet' ? <TabletIcon /> : <MobileIcon />}
                      </Button>
                    ))}
                    {project.preview_url && (
                      <Button variant="outlined" color="primary" size="sm" endDecorator={<OpenInNewIcon />} onClick={() => window.open(project.preview_url, '_blank')} sx={{ ml: 1 }}>Preview</Button>
                    )}
                  </Box>
                </Box>
                <Box sx={{ overflow: 'hidden', mx: 'auto', width: previewDevice === 'desktop' ? '100%' : previewDevice === 'tablet' ? 768 : 375, height: 700, bgcolor: '#ffffff', position: 'relative', borderTop: '1px solid', borderColor: 'divider' }}>
                  {isRevising && (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.6)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <CircularProgress size="lg" />
                      <Typography level="title-lg" sx={{ color: 'white' }}>Applying your revisions...</Typography>
                      <Typography level="body-sm" sx={{ color: 'neutral.300' }}>This may take a couple of minutes</Typography>
                    </Box>
                  )}
                  {project.preview_url ? (
                    <iframe src={project.preview_url} style={{ width: '100%', height: '100%', border: 'none' }} title="Website Preview" />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography level="body-md" sx={{ color: 'text.secondary' }}>Preview not available</Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </TabPanel>

            {/* TAB 1: Page Templates */}
            <TabPanel value={1} sx={{ p: 0 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography level="h3" sx={{ mb: 1 }}>Page Templates</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                    Review and edit the HTML templates that will be used for each page type.
                    Content will be injected into {'{{placeholder}}'} markers during generation.
                  </Typography>
                  {project.page_templates && Object.keys(project.page_templates).length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {Object.entries(project.page_templates).map(([key, template]) => {
                        const mode = templateViewMode[key] || 'preview';
                        const blobUrl = templateBlobUrls[key] || '';

                        return (
                          <Sheet key={key} variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography level="title-md" sx={{ textTransform: 'capitalize' }}>
                                {key.replace(/_/g, ' ')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="sm"
                                  variant={mode === 'preview' ? 'solid' : 'outlined'}
                                  onClick={() => setTemplateViewMode(prev => ({ ...prev, [key]: 'preview' }))}
                                >
                                  Preview
                                </Button>
                                <Button
                                  size="sm"
                                  variant={mode === 'code' ? 'solid' : 'outlined'}
                                  startDecorator={<CodeIcon />}
                                  onClick={() => {
                                    setTemplateViewMode(prev => ({ ...prev, [key]: 'code' }));
                                    if (!editedTemplates[key]) {
                                      setEditedTemplates(prev => ({ ...prev, [key]: template }));
                                    }
                                  }}
                                >
                                  Code
                                </Button>
                              </Box>
                            </Box>
                            {mode === 'preview' ? (
                              <Box sx={{ position: 'relative', width: '100%', height: 420, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                                <iframe
                                  src={blobUrl}
                                  title={`Template preview: ${key}`}
                                  style={{
                                    width: '1280px',
                                    height: '1050px',
                                    border: 'none',
                                    transform: 'scale(0.5)',
                                    transformOrigin: 'top left',
                                    pointerEvents: 'none',
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box sx={{ p: 2 }}>
                                <CodeEditor
                                  value={editedTemplates[key] ?? template}
                                  onChange={(val) => setEditedTemplates(prev => ({ ...prev, [key]: val }))}
                                  language="html"
                                  height="400px"
                                />
                              </Box>
                            )}
                          </Sheet>
                        );
                      })}
                      {Object.keys(editedTemplates).length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            startDecorator={<SaveIcon />}
                            loading={savingTemplates}
                            onClick={async () => {
                              setSavingTemplates(true);
                              try {
                                const updatedTemplates = { ...project.page_templates, ...editedTemplates };
                                await api.websiteBuilder.updateProject(projectId!, { page_templates: updatedTemplates });
                                await refetchProject();
                                setEditedTemplates({});
                                setEditingTemplateKey(null);
                                toast.success('Templates saved!');
                              } catch {
                                toast.error('Failed to save templates');
                              } finally {
                                setSavingTemplates(false);
                              }
                            }}
                          >
                            Save Template Changes
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      No templates generated yet. Templates are created during the design system generation phase.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            {/* TAB 2: Shared Components */}
            <TabPanel value={2} sx={{ p: 0 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography level="h3" sx={{ mb: 1 }}>Shared Components</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                    Header, child page hero banner, and footer HTML shared across all pages.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {([
                      { label: 'Header HTML', field: 'shared_header_html' },
                      { label: 'Child Hero Banner', field: 'child_hero_html' },
                      { label: 'Footer HTML', field: 'shared_footer_html' },
                    ] as Array<{ label: string; field: SharedChromeField }>).map(({ label, field }) => (
                      <Box key={field}>
                        <Typography level="title-md" sx={{ mb: 1 }}>{label}</Typography>
                        <CodeEditor
                          value={editedShared[field] ?? project[field] ?? ''}
                          onChange={(val) => setEditedShared(prev => ({ ...prev, [field]: val }))}
                          language="html"
                          height="250px"
                        />
                      </Box>
                    ))}
                    {Object.keys(editedShared).length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          startDecorator={<SaveIcon />}
                          loading={savingTemplates}
                          onClick={async () => {
                            setSavingTemplates(true);
                            try {
                              await api.websiteBuilder.updateProject(projectId!, editedShared);
                              await refetchProject();
                              setEditedShared({});
                              toast.success('Shared components saved!');
                              // Saving only updates the project fields — the
                              // already-assembled pages still carry the old
                              // header/footer until a republish
                              if (window.confirm(
                                'Apply the new header/footer to all existing pages now? '
                                + '(takes seconds, no AI involved)',
                              )) {
                                try {
                                  await api.websiteBuilder.republishChrome(projectId!, { redeploy: false });
                                  toast.success('Header/footer republished to all pages');
                                } catch {
                                  toast.error('Failed to republish header/footer');
                                }
                              }
                            } catch {
                              toast.error('Failed to save shared components');
                            } finally {
                              setSavingTemplates(false);
                            }
                          }}
                        >
                          Save Changes
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>
          </Tabs>

          {/* Feedback & Actions */}
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                Feedback & Revisions
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                Love it? Click &quot;Generate Entire Site&quot; to create all pages. Want changes? Describe them below.
              </Typography>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="E.g., 'Make the hero section taller', 'Change the CTA button color to red', 'Add more emphasis on emergency services'"
                minRows={3}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setConfirmRegenDesignOpen(true)}
                  loading={generateHomepage.isPending}
                  disabled={isRevising}
                >
                  Regenerate Design
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<RefreshIcon />}
                  onClick={() => revise.mutate()}
                  loading={revise.isPending || isRevising}
                  disabled={!feedback.trim() || isRevising}
                >
                  {isRevising ? 'Revising...' : 'Revise Homepage'}
                </Button>
                <Button
                  size="lg"
                  startDecorator={<AutoAwesomeIcon />}
                  onClick={() => generateSite.mutate()}
                  loading={generateSite.isPending}
                  disabled={isRevising}
                  color="success"
                >
                  Generate Entire Site
                </Button>
              </Box>
              {project.revision_count > 0 && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                  {project.revision_count} revision(s) made
                </Typography>
              )}
            </CardContent>
          </Card>

        </Box>
      )}

      {/* SITE REVIEW */}
      {project?.status === 'site_review' && (() => {
        const selectedPage = (project.pages || []).find(
          (p) => p.slug === previewPageSlug
        );
        const currentPreviewUrl = selectedPage?.preview_url || project.preview_url;
        const currentPageTitle = selectedPage?.title || 'Homepage';

        return (
        <Box>
          <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography level="h3">Full Site Preview</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    endDecorator={<OpenInNewIcon />}
                    onClick={() => window.open(currentPreviewUrl, '_blank')}
                    disabled={!currentPreviewUrl}
                  >
                    Open in New Tab
                  </Button>
                </Box>
              </Box>

              {/* Page navigation tabs */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  size="sm"
                  variant={!previewPageSlug ? 'solid' : 'outlined'}
                  color={!previewPageSlug ? 'primary' : 'neutral'}
                  onClick={() => setPreviewPageSlug('')}
                  sx={{ cursor: 'pointer' }}
                >
                  Homepage
                </Chip>
                {(project.pages || [])
                  .filter((page) => page.slug && page.status === 'generated')
                  .map((page) => (
                    <Chip
                      key={page.id}
                      size="sm"
                      variant={previewPageSlug === page.slug ? 'solid' : 'outlined'}
                      color={previewPageSlug === page.slug ? 'primary' : 'neutral'}
                      onClick={() => setPreviewPageSlug(page.slug)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {page.title}
                    </Chip>
                  ))}
              </Box>

              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Viewing: {currentPageTitle} {previewPageSlug ? `(/${previewPageSlug})` : '(/)'}
              </Typography>
            </Box>

              {/* Preview iframe */}
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  height: 700,
                  bgcolor: '#ffffff',
                }}
              >
                {currentPreviewUrl ? (
                  <iframe
                    key={previewPageSlug}
                    src={currentPreviewUrl}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title={`Preview: ${currentPageTitle}`}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography>Preview not available</Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', p: 2 }}>
                <Button
                  size="lg"
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`/marketing/website-builder/${projectId}/manage`)}
                >
                  Manage Website
                </Button>
                <Button
                  size="lg"
                  startDecorator={<RocketIcon />}
                  onClick={() => deploy.mutate()}
                  loading={deploy.isPending}
                  color="success"
                >
                  Deploy to Production
                </Button>
              </Box>
          </Card>
        </Box>
        );
      })()}

      {/* DEPLOYED */}
      {project?.status === 'deployed' && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckIcon size={64} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
            <Typography level="h2" sx={{ mb: 1 }}>
              Your Website is Live!
            </Typography>
            <Typography level="body-lg" sx={{ color: 'text.secondary', mb: 3 }}>
              Your AI-generated website has been deployed and is now accessible.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              {project.production_url && (
                <Button
                  size="lg"
                  variant="solid"
                  onClick={() => window.open(project.production_url, '_blank')}
                  endDecorator={<OpenInNewIcon />}
                >
                  Visit Your Website
                </Button>
              )}
              <Button
                size="lg"
                variant="outlined"
                color="primary"
                onClick={() => navigate(`/marketing/website-builder/${projectId}/manage`)}
              >
                Manage Website
              </Button>
            </Box>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {project.production_url}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="plain"
                color="neutral"
                size="sm"
                startDecorator={<RefreshIcon />}
                onClick={() => setConfirmRegenDesignOpen(true)}
                loading={generateHomepage.isPending}
              >
                Regenerate Design
              </Button>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                Rebuilds the whole design with your latest data — the live site keeps
                serving the current version until you deploy the new one.
              </Typography>
            </Box>
            {isRecentlyDeployed(project.updated_at) && (
              <Alert color="neutral" variant="soft" sx={{ mt: 3, textAlign: 'left' }}>
                <Box>
                  <Typography level="title-sm">Just published?</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    A brand-new site can take up to an hour to come online everywhere
                    while hosting and CDN records propagate. If the link shows an
                    access error or redirects oddly at first, give it a little time
                    and try again — no action needed on your part.
                  </Typography>
                </Box>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Regenerate design confirmation (reachable from homepage review AND deployed) */}
      <Modal open={confirmRegenDesignOpen} onClose={() => setConfirmRegenDesignOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Regenerate the entire design?</DialogTitle>
          <DialogContent>
            This rebuilds the whole design system — colors, typography, page templates, and the
            shared header/footer — using your latest company data, then regenerates the homepage.
            Your current design and any template edits will be replaced, and site pages will need
            to be regenerated afterwards.
            {project?.status === 'deployed' && (
              <> Your live website keeps serving the current version untouched until you deploy
              the new one.</>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              color="danger"
              onClick={() => {
                setConfirmRegenDesignOpen(false);
                generateHomepage.mutate({ regenerateDesign: true });
              }}
            >
              Regenerate Design
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setConfirmRegenDesignOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

    </Box>
  );
};
